import * as React from 'react'
import { Plus } from 'lucide-react'

import type { Discount } from '@/gen/cortado/v1/invoices_pb'
import { DiscountTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@/components/ui/select'
import { DiscountCreateSheet } from '@/components/settings/discount-create-sheet'
import { toast } from 'sonner'
import { formatDollars } from '@/lib/currency-utils'

const ZERO_UUID = '00000000-0000-0000-0000-000000000000'

interface DiscountSelectorProps {
  value?: string
  discounts: Discount[]
  organizationId: string
  inlineDiscount?: {
    name: string
    type: DiscountTypeEnum
    value: number
  }
  onChange: (discountId?: string) => void
  onInlineDiscountChange?: (discount?: {
    name: string
    type: DiscountTypeEnum
    value: number
  }) => void
  disabled?: boolean
  supportsInlineDiscounts?: boolean
}

/**
 * DiscountSelector - Discount dropdown with inline/saved support
 *
 * Features:
 * - ZERO_UUID pattern for "No Discount"
 * - Saved discounts from organization
 * - Optional inline discount display (quote-specific)
 * - "Add New Discount..." button
 * - Proper value formatting (% vs $)
 */
export function DiscountSelector({
  value,
  discounts,
  organizationId,
  inlineDiscount,
  onChange,
  onInlineDiscountChange,
  disabled = false,
  supportsInlineDiscounts = true,
}: DiscountSelectorProps) {
  const [isDiscountSheetOpen, setIsDiscountSheetOpen] = React.useState(false)

  // Check if there's an inline discount (one-time use)
  // Requires actual data: name must exist AND value must be positive
  // Handle both undefined and empty string as "no discount"
  const hasNoDiscountId = !value || value === '' || value === ZERO_UUID
  const hasInlineDiscount = inlineDiscount &&
    inlineDiscount.name &&
    inlineDiscount.value > 0 &&
    hasNoDiscountId

  // Determine the select value - use zero UUID for "No discount"
  // Handle empty string as equivalent to no discount
  const selectValue = hasInlineDiscount ? 'inline-discount' : (value && value !== '' ? value : ZERO_UUID)

  const handleValueChange = (newValue: string) => {
    if (newValue === ZERO_UUID) {
      // Clear all discount fields
      onChange(undefined)
      onInlineDiscountChange?.(undefined)
    } else if (newValue !== 'inline-discount') {
      // Selected a saved discount - populate all fields for totals calculation
      const selectedDiscount = discounts.find(d => d.id === newValue)
      if (selectedDiscount) {
        onChange(newValue)
        // Also populate name, type, and value so totals hook can calculate correctly
        onInlineDiscountChange?.({
          name: selectedDiscount.name,
          type: selectedDiscount.discountType as DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE | DiscountTypeEnum.DISCOUNT_TYPE_FIXED,
          value: selectedDiscount.value,
        })
      }
    }
    // If value is 'inline-discount', do nothing (it's already set in form state)
  }

  const handleDiscountCreated = (discount: Discount, saveForFutureUse: boolean) => {
    if (supportsInlineDiscounts) {
      // Quote mode: support both saved and inline discounts
      if (saveForFutureUse) {
        // Saved discount: set ID and populate all fields for totals calculation
        onChange(discount.id)
        onInlineDiscountChange?.({
          name: discount.name,
          type: discount.discountType as DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE | DiscountTypeEnum.DISCOUNT_TYPE_FIXED,
          value: discount.value,
        })
      } else {
        // Inline discount: set name/type/value directly, no ID
        onChange(undefined)
        onInlineDiscountChange?.({
          name: discount.name,
          type: discount.discountType as DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE | DiscountTypeEnum.DISCOUNT_TYPE_FIXED,
          value: discount.value,
        })
      }
      toast.success(`Discount "${discount.name}" applied`)
    } else {
      // Invoice mode: support both saved and inline discounts
      onChange(saveForFutureUse ? discount.id : undefined)
      onInlineDiscountChange?.({
        name: discount.name,
        type: discount.discountType as DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE | DiscountTypeEnum.DISCOUNT_TYPE_FIXED,
        value: discount.value,
      })
      toast.success(`Discount "${discount.name}" added`)
    }
  }

  return (
    <>
      <div className="grid min-w-0 gap-3">
        <Label htmlFor="discount">Discount</Label>
        <Select
          value={selectValue}
          onValueChange={handleValueChange}
          disabled={disabled}
        >
          <SelectTrigger
            id="discount"
            className="w-full min-w-0 overflow-hidden [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:truncate"
          >
            <SelectValue placeholder="No discount selected" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ZERO_UUID}>No Discount</SelectItem>
            <SelectSeparator />
            {discounts
              .filter((discount) => discount.isActive)
              .map((discount) => {
                // Format discount value with appropriate symbol
                const isPercentage = discount.discountType === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE
                const valueDisplay = isPercentage
                  ? `${discount.value}%`
                  : `$${formatDollars(discount.value)}`

                return (
                  <SelectItem key={discount.id} value={discount.id}>
                    {valueDisplay} - {discount.name}
                  </SelectItem>
                )
              })}

            {/* Show inline discount if present */}
            {hasInlineDiscount && (
              <>
                <SelectSeparator />
                <SelectItem value="inline-discount">
                  {(() => {
                    // Format inline discount value with appropriate symbol
                    const isPercentage = inlineDiscount.type === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE
                    const discountValue = inlineDiscount.value
                    const valueDisplay = isPercentage
                      ? `${discountValue}%`
                      : `$${formatDollars(discountValue)}`

                    return `${valueDisplay} - ${inlineDiscount.name} (One-time use)`
                  })()}
                </SelectItem>
              </>
            )}

            <SelectSeparator />
            <div
              className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsDiscountSheetOpen(true)
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Discount...
            </div>
          </SelectContent>
        </Select>
      </div>

      <DiscountCreateSheet
        organizationId={organizationId}
        discount={null}
        open={isDiscountSheetOpen}
        onOpenChange={setIsDiscountSheetOpen}
        onSuccess={handleDiscountCreated}
      />
    </>
  )
}
